import { directive } from "../index";
import validator from "validator";
import Inputmask from "inputmask";
import getCookie from "../../../utils/getCookie";
import { load } from "recaptcha-v3";
import { trackEvent } from "../../../modules/track";
import scrollToElement from "../../../utils/scrollToElement";

var language = document.getElementsByTagName("html")[0].getAttribute("lang");

const validators = {
    checkbox: {
        required: el => el.checked
    },
    radio: {
        required: el => el.checked
    },
    fieldset: {
        required: el => el.dataset.value
    },
    select: {
        required: el => el.value !== ""
    },
    string: {
        date: el => validator.isDate(new Date(el.value), { format: "MM/DD/YYYY" }),
        required: el => !validator.isEmpty(el.value),
        email: el => validator.isEmail(el.value),
        zip: el => validator.isLength(el.value, { min: 5, max: 5 }),
        patientZIPEl: el => !validator.isEmpty(el.value),
        phone: el => (validator.isEmpty(el.value) ? true : validator.isMobilePhone(el.value)),
        minMaxLen: el =>
            validator.isLength(el.value, {
                min: el.dataset.min,
                max: el.dataset.max
            }),
        number: el => validator.isInt(el.value),
        overEighteen: el => validator.isBefore(new Date(el.value).toString(), new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toString()),
        overSix: el => validator.isBefore(new Date(el.value).toString(), new Date(new Date().setFullYear(new Date().getFullYear() - 6)).toString()),
        overSixMonths: el => validator.isBefore(new Date(el.value).toString(), new Date(new Date().setMonth(new Date().getMonth() - 6)).toString()),
        overTwelve: el => validator.isBefore(new Date(el.value).toString(), new Date(new Date().setFullYear(new Date().getFullYear() - 12)).toString()),
        overTwo: el => validator.isBefore(new Date(el.value).toString(), new Date(new Date().setFullYear(new Date().getFullYear() - 2)).toString()),
        overOne: el => validator.isBefore(new Date(el.value).toString(), new Date(new Date().setFullYear(new Date().getFullYear() - 1)).toString())
    }
};

directive("copayform", settings => {
    return {
        get config() {
            const config = Object.assign(
                {
                    errorMessages: {
                        overEighteen: language == "en-US" ? "The patient must be 18 years of age or older to sign up." : "El paciente debe tener 18 años o más para inscribirse.",
                        overTwelve: language == "en-US" ? "The patient must be 12 years of age or older to sign up." : "El paciente debe tener 12 años o más para inscribirse.",
                        overSix: language == "en-US" ? "The patient must be 6 years of age or older to sign up." : "El paciente debe tener 6 años o más para inscribirse.",
                        overTwo: language == "en-US" ? "The patient must be 2 years of age or older to sign up." : "El paciente debe tener 2 años o más para inscribirse.",
                        overOne: language == "en-US" ? "The patient must be 1 year of age or older to sign up." : "El paciente debe tener 1 año o más para inscribirse.",
                        overSixMonths: language == "en-US" ? "The patient must be 6 months of age or older to sign up." : "El paciente debe tener 6 meses o más para inscribirse.",
                        default: language == "en-US" ? "Invalid input" : "Entrada inválida",
                        date: language == "en-US" ? "Please enter a valid date." : "Por favor, ingresa una fecha de nacimiento válida.",
                        zip: language == "en-US" ? "Please enter a valid ZIP Code." : "Por favor, ingresa un código postal válido.",
                        validZip: language == "en-US" ? "Please enter a valid US, District of Columbia, Puerto Rico, Guam, or USVI ZIP Code." : "Por favor, ingresa un código postal válido de los Estados Unidos, el Distrito de Columbia, Puerto Rico, Guam o las Islas Vírgenes de los Estados Unidos.",
                        patientZIPEl: language == "en-US" ? "Please enter a valid US, District of Columbia, Puerto Rico, Guam, or USVI ZIP Code." : "Por favor, ingresa un código postal válido de los Estados Unidos, el Distrito de Columbia, Puerto Rico, Guam o las Islas Vírgenes de los Estados Unidos.",
                        state: language == "en-US" ? "Please select a state" : "Por favor, selecciona un estado.",
                        required: language == "en-US" ? "This field is required" : "Este campo es obligatorio",
                        email: language == "en-US" ? "Please enter a valid email address. Your copay card will be sent to the email address you provide." : "Por favor, ingresa una dirección de correo electrónico válida. La tarjeta de copago se te enviará a la dirección de correo electrónico que proporciones.",
                        safeToSend: language == "en-US" ? "Please enter a valid email address. Your copay card will be sent to the email address you provide." : "Por favor, ingresa una dirección de correo electrónico válida. La tarjeta de copago se te enviará a la dirección de correo electrónico que proporciones.",
                        phone: language == "en-US" ? "Please enter a valid phone number." : "Por favor, ingresa un número de teléfono válido.",
                        minMaxLen: language == "en-US" ? "Invalid ID#" : "Número de identificación no válido", // This is only being used for cardID
                        number: "Please enter a number",
                        password: "Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 digit",
                        passwordMatch: "Passwords do not match",
                        gender: "Please select a gender.",
                        caregiverPhone: "Please enter a valid phone number.",
                        validateCardId: {
                            E502: language == "en-US" ? "Invalid ID#" : "Número de identificación no válido",
                            E503: language == "en-US" ? "Invalid ID#" : "Número de identificación no válido",
                            E541: language == "en-US" ? "Invalid ID#" : "Número de identificación no válido",
                            E101: language == "en-US" ? "Invalid ID#" : "Número de identificación no válido",
                            copayValidateAPIFail: language == "en-US" ? "Invalid ID#" : "Número de identificación no válido."
                        }
                    }
                },
                settings || {}
            );
            return config;
        },
        get selection() {
            let selection = this.getUrlParam("selection");
            let indication = this.getUrlParam("indication");

            return selection || indication || "atopicdermatitis";
        },
        get isPreSelected() {
            return this.getUrlParam("indication") ? true : false;
        },

        formData: {
            // Defaults
            language: language == "en-US" ? "EN_US" : "ES_US",
            address2: "",
            crm: true,
            indication: "atopicdermatitis"
        },
        cardAction: "",
        baseZipValidatorUrl: "/.rest/usps/addresses/v1/zip/",
        baseSafeToSendUrl: "/.rest/freshaddress/validateEmail?email=",
        submitUrl: "/.rest/copay/",
        validateCardUrl: "/.rest/copay/validate/",
        //signup Paths
        signUpPath: "/patient-resources/sign-up",
        signUpPathAD: "/sign-up",
        signUpPathADES: "/suscripcion",
        signUpPathASES: "/recursos-pacientes/inscripcion",
        signUpPathPN: "/dupixent-resources/sign-up",

        formStep: 1,
        stepErrorMessage: language == "en-US" ? "Please correct errors noted above to proceed." : "Por favor, corrige los errores indicados arriba para continuar.",
        cachedFormData: {},
        errorMessages: {},
        eligibilityStatus: "",
        errors: {},
        apiValidRules: ["safeToSend", "validZip", "validateCardId"],
        yesTakingPaitent: "Yes, I have started taking DUPIXENT",
        stepError: "",
        caregiverInputErrorsMade: false,
        isSubmitting: false,
        isValidCardId: false,
        errored: [],
        backUsed: false,
        init: function() {
            $('[data-toggle="tooltip"]').tooltip({
                placement: this.mediaQuery(519) ? "bottom" : "right",
                html: true,
                trigger: "hover",
                container: "body"
            });

            this.env();
            this.assignInputsInStep();

            this.inputElements = [...this.$el.querySelectorAll("input[data-rules]"), ...this.$el.querySelectorAll("fieldset[data-rules]"), ...this.$el.querySelectorAll("select[data-rules]")];
            this.errorMessages = { ...this.config.errorMessages };

            this.defaultErrorMsg();
            this.preSetForm();
            this.getIndicationJobCode();

            Inputmask().mask(document.querySelectorAll("input"));

            this.mediaOriginCode();

            phoneInputMask();

            window.applyTest = arg => this.applyTest(arg);
            window.applyTestData = arg => this.applyTestData(arg);
            window.logTestResults = () => this.logTestResults();
            window.clearTestResults = () => this.clearTestResults();
            window.bumpTestData = () => this.bumpTestData();

            window.postFormData = token => this.postFormData(token);
        },
        mediaQuery(number) {
            var isMobile = window.matchMedia(`(max-width: ${number}px)`);
            return isMobile.matches;
        },
        mediaOriginCode() {
            let mocCodeDash = this.getUrlParam("moc-code");
            let mocCode = this.getUrlParam("moccode");
            let mocCodeCamel = this.getUrlParam("mocCode");
            let mocUppercase = this.getUrlParam("MOC");
            let mocLowercase = this.getUrlParam("moc");
            let mocCookie = getCookie("QueryString__MOC");

            if (mocCodeDash) {
                this.formData.mediaOriginCode = mocCodeDash;
            }
            if (mocCode) {
                this.formData.mediaOriginCode = mocCode;
            }
            if (mocCodeCamel) {
                this.formData.mediaOriginCode = mocCodeCamel;
            }
            if (mocUppercase) {
                this.formData.mediaOriginCode = mocUppercase;
            }
            if (mocLowercase) {
                this.formData.mediaOriginCode = mocLowercase;
            }
            if (mocCookie) {
                this.formData.mediaOriginCode = mocCookie;
            }
        },
        env() {
            const winOrigin = window.location.origin;
            this.baseEndPtUrl = settings.contextPath ? winOrigin + settings.contextPath : (this.baseEndPtUrl = winOrigin);
        },
        // START TESTING
        incrementTestCase() {
            let curInStorage = Number(localStorage.getItem("testItem")) || 0;
            curInStorage = curInStorage + 1;
            localStorage.setItem("testItem", curInStorage);
        },
        testsready() {
            let self = this;

            this.testData = window.testData;

            let curInStorage = Number(localStorage.getItem("testItem")) || 0;
            let numOfTests = Object.keys(this.testData).length;
            numOfTests = Number(numOfTests) - 1;

            if (curInStorage == "0") {
                localStorage.setItem("testResults", "[]");
                localStorage.setItem("testItem", "0");
            }

            localStorage.setItem("numOfTests", numOfTests);
            console.log(`Running ${numOfTests} different tests`); // eslint-disable-line

            if (curInStorage <= numOfTests) {
                self.applyTest(this.testData[curInStorage].name);
            } else {
                this.logTestResults();
            }
        },
        bumpTestData() {
            this.formData = { ...this.formData, ...this.applyTestData() };
        },
        applyTestData(data) {
            var dateString = Date.now();

            const formData = Object.assign({}, data?.data || {}, {
                address1: "1134 N 9th",
                address2: "",
                city: "Milwaukee",
                state: "WI",
                zip: "53233",
                patientZIPEl: "53233",
                patientFirstname: `FN ${dateString}`,
                patientLastname: `LN ${dateString}`,
                mediaOriginCode: this.formData.mediaOriginCode,

                email: `${dateString}@crescendocollective.com`,
                trueCompletAc: true
            });

            this.testData = data;
            return formData;
        },
        applyTest(targetTestDataStep) {
            this.testData = window.testData;

            const findTestCaseName = testName => this.testData.find(e => e.name == testName);
            const findTestIndex = testName => this.testData.findIndex(e => e.name == testName);

            if (findTestCaseName(targetTestDataStep)) {
                console.log(`Running test ${findTestIndex(targetTestDataStep)}: ${targetTestDataStep}`); // eslint-disable-line
                this.formData = this.applyTestData(findTestCaseName(targetTestDataStep));

                this.formStep = 5;

                if (this.cardAction == "ACTIVATE") {
                    const cardId = this.getLocalTestCardId();
                    if (cardId) {
                        this.formData.cardID = cardId;
                        this.finalSubmit();
                    } else {
                        console.warn("No card ID"); // eslint-disable-line
                    }
                } else {
                    this.finalSubmit();
                }

                // if (this.isAllValid()) {
                //     this.finalSubmit();
                // } else {
                //     console.warn("FORM HAS ERRORS"); // eslint-disable-line
                // }
            }
        },
        getLocalTestCardId() {
            const localCardIds = JSON.parse(localStorage.getItem("testCardIds")) || [];
            let cardId;

            if (localCardIds.length > 0) {
                cardId = localCardIds.pop();
                localStorage.setItem("testCardIds", JSON.stringify(localCardIds));
                return cardId;
            }

            return false;
        },
        makeTestPipe() {
            const isDateMoreThanYears = (testDate, years) => {
                return new Date(testDate.getFullYear() + years, testDate.getMonth() - 1, testDate.getDate()) <= new Date();
            };

            const pipeNames = {
                indication: data => data.indication,
                patientOrCaregiver: data => data.patientOrCaregiver,
                prescriptionStatus: data => data.prescriptionStatus,
                insuranceType: data => data.insuranceType,
                cardAction: data => data.cardAction,
                gender: data => data.gender,
                patientDOB: data => {
                    const dob = new Date(data.patientDOB);
                    const isOver6 = isDateMoreThanYears(dob, 6);
                    const isOver12 = isDateMoreThanYears(dob, 12);
                    const isOver18 = isDateMoreThanYears(dob, 18);

                    if (isOver18) {
                        return "ADULT";
                    } else if (isOver12) {
                        return "ADOLESCENT";
                    } else if (isOver6) {
                        return "PEDIATRIC";
                    }
                    return "INELIGIBLE_AGE";
                },
                crm: data => (data.crm ? "CRM_IN" : "CRM_OUT"),
                smsUpdates: data => (data.smsUpdates ? "SMS_IN" : "SMS_OUT")
            };

            const pipeString = Object.entries(pipeNames)
                .map(pipeItem => pipeItem[1](this.formData))
                .join(".");

            return pipeString;
        },
        isTesting() {
            return localStorage.getItem("test") == "true";
        },
        logResult(result) {
            if (this.isTesting()) {
                // Append Results
                let results = JSON.parse(localStorage.getItem("testResults")) || [];
                const testName = `${this?.testData?.name || this.makeTestPipe()}`;

                results.push({
                    testName: testName,
                    testNum: window.testData.findIndex(test => test.name == testName) ?? "??",
                    ...this.formData,
                    cardId: result?.mckesson?.cardID,
                    mckessonCode: result?.mckesson?.code,
                    mckessonSuccess: result?.mckesson?.success ?? false,
                    merkleSuccess: result?.merkle?.success ?? false,
                    merkleFormId: result?.merkle?.formID ?? "none"
                });

                localStorage.setItem("testResults", JSON.stringify(results));

                // console.table(results);

                console.log(JSON.stringify({ name: testName, data: { ...this.formData } })); // eslint-disable-line

                console.log("Test done."); // eslint-disable-line

                if (localStorage.getItem("test_autorun") == "true") {
                    this.incrementTestCase();

                    // let reloadCountDown = 5;
                    // setInterval(function() {
                    //     reloadCountDown--;
                    //     if (reloadCountDown == 0) {
                    //         window.location.reload();
                    //     }

                    //     console.log(`Reloading in ${reloadCountDown} seconds`);
                    // }, 1000);
                }
            }
        },
        clearTestResults() {
            localStorage.setItem("testResults", JSON.stringify([]));
        },
        logTestResults() {
            let results = JSON.parse(localStorage.getItem("testResults"));
            console.table(results); // eslint-disable-line

            // Choose sorting of the columns in exported sheet
            const columnsSorter = [{ key: "testName", label: "Test key" }, { key: "testNum", label: "Test Num" }, { key: "indication", label: "Select condition" }, { key: "patientOrCaregiver" }, { key: "patientDOB", label: "DOB" }, { key: "patientZIPEl", label: "Patient Zip" }, { key: "prescriptionStatus" }, { key: "insuranceType" }, { key: "cardAction" }, { key: "patientFirstname" }, { key: "patientLastname" }, { key: "gender" }, { key: "email" }, { key: "address1" }, { key: "address2" }, { key: "zip" }, { key: "city" }, { key: "state" }, { key: "mckessonSuccess" }, { key: "mckessonCode" }, { key: "cardId" }, { key: "merkleSuccess" }, { key: "merkleFormId" }];

            // Find all keys in test data
            var allKeys = new Set();
            results.forEach(function(r) {
                Object.keys(r).forEach(function(k) {
                    allKeys.add(k);
                });
            });

            // Find anything we did not map
            const unMappedValues = Array.from(allKeys).filter(e => {
                const isMatch = columnsSorter.find(col => col.key == e);
                return !isMatch;
            });
            // Append unmapped items to end
            unMappedValues.forEach(e => columnsSorter.push({ key: e }));

            // Make CVS header
            let csvContent = "data:text/csv;charset=utf-8,";
            let head = columnsSorter.map(e => e.label || e.key).join(",");
            csvContent += head + "\r\n";

            // Append each row
            results.forEach(function(rowArray) {
                let ar = [];
                columnsSorter.forEach(row => {
                    ar.push(rowArray[row.key]);
                });

                // Do all keys
                // allKeys.forEach(k => {
                //     ar.push(rowArray[k]);
                // });

                csvContent += ar.join(",") + "\r\n";
            });

            var encodedUri = encodeURI(csvContent);
            // window.open(encodedUri);

            var link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `${new Date().toLocaleDateString().replace(/\//g, "-")}_Dupixent.csv`);
            document.body.appendChild(link); // Required for FF

            link.click();
        },
        // END TESTS
        backStep() {
            if (this.formStep == 2) {
                this.clearStepTwo();
            }
            this.formStep = this.formStep - 1;
            this.backUsed = true;
        },
        nextStep() {
            if (this.isTesting()) {
                console.log("this.formData", this.formData); // eslint-disable-line
            }
            switch (this.formStep) {
                case 1:
                    trackEvent({
                        event: "copay_prescription_info_complete",
                        event_name: "copay_prescription_info_complete",
                        option_selected: this.formData.patientOrCaregiver,
                        step_completed: this.formStep
                    });
                    this.nextStepHelper();
                    break;
                case 2:
                    trackEvent({
                        event: "copay_eligibility_complete",
                        event_name: "copay_eligibility_complete",
                        step_completed: this.formStep
                    });
                    this.nextStepHelper();
                    break;
                case 3:
                    trackEvent({
                        event: "copay_card_info_complete",
                        event_name: "copay_card_info_complete",
                        option_selected: this.cardAction,
                        step_completed: this.formStep
                    });
                    this.nextStepHelper();
                    break;
                case 4:
                    trackEvent({
                        event: "copay_contact_info_complete",
                        event_name: "copay_contact_info_complete",
                        step_completed: this.formStep
                    });
                    this.nextStepHelper();
                    break;
                case 5:
                    trackEvent({
                        event: "copay_form_submit",
                        event_name: "copay_form_submit",
                        step_completed: this.formStep
                    });
                    this.finalSubmit();
                    break;
                default:
                    this.nextStepHelper();
            }
        },
        nextStepHelper() {
            this.formStep = this.formStep + 1;
        },
        handleScroll() {
            if (this.formStep === 1 && !this.backUsed) return;
            this.$nextTick(function() {
                setTimeout(function() {
                    scrollToElement(".multistepform");
                }, 0);
            });
        },
        finalSubmit() {
            var recaptchaV3String = $("button[name='recaptcha']").val();
            var recaptchaV3Boolean = recaptchaV3String === "true";

            if (recaptchaV3Boolean) {
                const recaptchaID = document.querySelector(".multistepform__submitBtn").dataset.sitekey;
                load(recaptchaID).then(recaptcha => {
                    recaptcha.execute("submit").then(token => {
                        this.postData(token, recaptchaV3Boolean);
                    });
                });
            } else {
                let token = false;
                this.postData(token, recaptchaV3Boolean);
            }
        },
        postData(token, recaptcha) {
            if (this.isTesting()) {
                console.log({ ...this.formData }); // eslint-disable-line
            }

            let self = this;
            let data = this.buildFormData(token, recaptcha);
            let url = this.baseEndPtUrl + this.submitUrl;
            this.isSubmitting = true;

            submit(url, data)
                .then(result => {
                    this.logResult(result);

                    let mckessonSuccess = result.mckesson.success;
                    let merkleSuccess = result.merkle.success;

                    let completeSuccess = mckessonSuccess && merkleSuccess;

                    if (completeSuccess) {
                        self.formStep = 6;
                    } else {
                        if (!this.isTesting()) {
                            window.location = settings.errorPageLink;
                        }

                        this.isSubmitting = false;
                    }
                })
                .catch(err => {
                    console.warn("Error during submission!", err); // eslint-disable-line
                    this.isSubmitting = false;

                    if (!this.isTesting()) {
                        window.location = settings.errorPageLink;
                    }
                });
        },
        isSuccessStep() {
            let wrapEl = document.querySelector(".multistepform");
            wrapEl.classList.add("multistepform--successful");
        },
        isStepActive(step) {
            if (this.formStep == 6) {
                this.isSuccessStep();
            }
            return step === this.formStep;
        },
        // Validate Step Helpers
        inputsInStep(step) {
            return Object.entries(this.errors).filter(ar => {
                if (ar[1].instep == step) {
                    return ar[1];
                }
            });
        },
        stepHasErrors(inputs) {
            return inputs.some(ar => {
                return ar[1].hasError;
            });
        },
        stepHasEmptyValues(inputs) {
            return inputs.some(ar => {
                return ar[1].value == "";
            });
        },
        copayNotEligibleTrackingHelper() {
            trackEvent({
                event: "copay_not_eligible_message",
                event_name: "copay_not_eligible_message"
            });
        },
        // ONLY For Step 2
        checkEligibility(emailLink) {
            trackEvent({
                event: "copay_eligibility_check_click",
                event_name: "copay_eligibility_check_click"
            });
            let hasPrescription = this.formData.prescriptionStatus;
            let insurance = this.formData.insuranceType;
            if (hasPrescription == "CONVERSION") {
                this.eligibilityStatus = "ineligiblePrescription";
                this.copayNotEligibleTrackingHelper();
            }
            if ((insurance == "COMMERCIAL" && hasPrescription == "ADHERENCE") || (insurance == "COMMERCIAL" && hasPrescription == "LIMBO")) {
                this.eligibilityStatus = "eligible";
            }

            if ((insurance == "GOVERNMENT" && hasPrescription == "ADHERENCE") || (insurance == "GOVERNMENT" && hasPrescription == "LIMBO")) {
                this.eligibilityStatus = "ineligibleInsurance";
                this.copayNotEligibleTrackingHelper();
            }

            if ((insurance == "NONE" && hasPrescription == "ADHERENCE") || (insurance == "NONE" && hasPrescription == "LIMBO")) {
                this.eligibilityStatus = "ineligibleInsurance";
                this.copayNotEligibleTrackingHelper();
            }
            this.getSignUpLink(emailLink);
            let buttonElement = document.querySelector(".subscribe-link");
            buttonElement.addEventListener("click", () => {
                //click signup cta
                trackEvent({
                    event: "email_signup_click",
                    event_name: "email_signup_click"
                });
            });
        },
        notEligible() {
            return this.eligibilityStatus == "ineligibleInsurance" || this.eligibilityStatus == "ineligiblePrescription";
        },
        // End For Step 2
        // Validating Steps 1 + 2 + 5 and 4 (Thru validateStep4())
        // use errored state to track error only once
        validateStep(step) {
            if (this.formStep == step) {
                let inputsInStep = this.inputsInStep(step);
                let hasErrors = this.stepHasErrors(inputsInStep);
                let hasEmptyVal = this.stepHasEmptyValues(inputsInStep);
                if (hasErrors) {
                    for (let index = 0; index < inputsInStep.length; index++) {
                        let elementHasError = inputsInStep[index][1].hasError;
                        if (elementHasError) {
                            let element = inputsInStep[index][0];
                            if (!this.errored.includes(element)) {
                                trackEvent({
                                    event: "form_error",
                                    event_name: "form_error",
                                    error_message: this.errors[element].errorMessage,
                                    form_type: "Copay Card Form"
                                });
                                this.errored.push(element);
                            }
                        }
                    }
                }
                this.stepError = hasErrors && hasEmptyVal ? step : "";
                if (this.stepError == 4) {
                    if (!this.errored.includes(".multistepform--stepError4")) {
                        this.errored.push(".multistepform--stepError4");
                        trackEvent({
                            event: "form_error",
                            event_name: "form_error",
                            error_message: this.stepErrorMessage,
                            form_type: "Copay Card Form"
                        });
                    }
                }
                if (this.stepError == 5) {
                    if (!this.errored.includes(".multistepform--stepError5")) {
                        this.errored.push(".multistepform--stepError5");
                        trackEvent({
                            event: "form_error",
                            event_name: "form_error",
                            error_message: this.stepErrorMessage,
                            form_type: "Copay Card Form"
                        });
                    }
                }
                return !hasErrors && !hasEmptyVal;
            }
        },
        ageValidation(auth, condition) {
            if (auth === "CAREGIVER" && condition === "ATOPICDERMATITIS") {
                return "date,overSixMonths";
            } else if ((auth === "CAREGIVER" && condition === "ASTHMA") || (auth === "CAREGIVER" && condition === "AFRS")) {
                return "date,overSix";
            } else if (auth === "CAREGIVER" && condition === "EOE") {
                return "date,overOne";
            } else if (auth === "PATIENT" || (auth === "CAREGIVER" && condition == "PN")) {
                return "date,overEighteen";
            } else if (auth === "CAREGIVER" && condition == "CRSWNP") {
                return "date,overTwelve";
            } else if (auth === "CAREGIVER" && condition == "CSU") {
                return document.querySelector("[data-csu-age-expansion]")?.dataset?.csuAgeExpansion === "true" ? "date,overTwo" : "date,overTwelve";
            } else if (auth === "CAREGIVER" && condition == "BP") {
                return "date,overEighteen";
            }
        },
        isAllValid() {
            return this.validateStep(1) && this.validateStep(2) && this.validateStep(3) && this.validateStep4() && this.validateStep(5);
        },
        validateStep4() {
            let self = this;
            if (this.formStep == 4) {
                if (this.formData.state && this.errors["state"].value == "") {
                    let stateInput = document.getElementsByName("state")[0];
                    this.validateInput(stateInput);
                }

                if (this.formData.city && this.errors["city"].value == "") {
                    let cityInput = document.getElementsByName("city")[0];
                    this.validateInput(cityInput);
                }

                if (this.formData.zip && this.errors["zip"].value == "") {
                    let zipInput = document.getElementsByName("zip")[0];
                    this.validateInput(zipInput);
                }

                // If we user selects caregiver we need to ensure the caregiver inputs are added to the errors array
                if (this.formData.patientOrCaregiver == "CAREGIVER" && !this.caregiverInputErrorsMade) {
                    var divElem = document.getElementById("multistepform__input");
                    var cgInputs = divElem.querySelectorAll("input, select");

                    this.inputElements = [...this.inputElements, ...cgInputs];
                    this.caregiverInputErrorsMade = true;
                    Array.from(cgInputs).forEach(function(el) {
                        self.makeError({ el });
                    });
                }
                if (this.formData.patientOrCaregiver == "PATIENT") {
                    delete this.errors["caregiverIsAdult"];
                    delete this.errors["caregiverFirstname"];
                    delete this.errors["caregiverLastname"];
                }
                return this.validateStep(4);
            }
        },
        preSetForm() {
            let indicationClass;
            switch (this.selection) {
                case "crswnp":
                    this.formData.indication = "CRSWNP";
                    indicationClass = "isCrswnp";
                    break;
                case "asthma":
                    this.formData.indication = "ASTHMA";
                    indicationClass = "isAsthma";
                    break;
                case "copd":
                    this.formData.indication = "COPD";
                    indicationClass = "isCOPD";
                    break;
                case "atopicdermatitis":
                    this.formData.indication = "ATOPICDERMATITIS";
                    indicationClass = "isAtopic";
                    break;
                case "eoe":
                    this.formData.indication = "EOE";
                    indicationClass = "isEoE";
                    break;
                case "prurigo-nodularis":
                case "pn":
                    this.formData.indication = "PN";
                    indicationClass = "isPn";
                    break;
                case "csu":
                    this.formData.indication = "CSU";
                    indicationClass = "isCSU";
                    break;
                case "bp":
                    this.formData.indication = "BP";
                    indicationClass = "isBP";
                    break;
                case "afrs":
                    this.formData.indication = "AFRS";
                    indicationClass = "isAFRS";
                    break;
                default:
                    indicationClass = "isFranchise";
                    break;
            }

            this.$el.classList.add(indicationClass);
        },
        async getIndicationJobCode() {
            await this.$nextTick();

            const jobCodeElement = document.querySelector(".approval-code");

            if (jobCodeElement && jobCodeElement.innerText.trim() !== "") {
                this.formData.matID = jobCodeElement.innerText.trim();
            }
        },
        setFieldSetVal(el) {
            let parentFieldset = document.querySelector(`fieldset[name=${el.name}]`);
            parentFieldset.dataset.value = el.value;
        },
        clearStepOne() {
            let indicationFieldset = this.$el.querySelector('fieldset[name="patientOrCaregiver"]');
            indicationFieldset ? (indicationFieldset.dataset.value = "") : "";

            this.formData.patientOrCaregiver = "";
            this.errors.patientOrCaregiver ? (this.errors.patientOrCaregiver.value = "") : "";
        },
        clearStepTwo() {
            let stepTwoInputs = this.inputsInStep(2);

            for (let index = 0; index < stepTwoInputs.length; index++) {
                const element = stepTwoInputs[index];
                const elName = element[0];
                const selector = `input[name="${elName}"]`;
                const domEl = document.querySelector(selector);

                this.formData[elName] = "";
                this.errors[elName] ? (this.errors[elName].value = "") : "";
                this.errors[elName] ? (this.errors[elName].hasError = false) : false;
                domEl.classList.remove("multistepform--invalid");
            }
        },
        clearEligibility() {
            this.eligibilityStatus = "";
        },
        validateInput(el) {
            let self = this;
            let inputType = this.getValidatorType(el);

            if (el.name === "caregiverPhone" && (el.value === "" || el.value.includes("X"))) {
                let phone = document.querySelector("#phone");
                phone.style.borderColor = "#d20606";
            }

            // Clear our error on empty mobile phone if smsUpdates is not checked
            if (el.name === "smsUpdates" && !el.checked && this.errors["mobile"]) {
                this.errors["mobile"].hasError = false;
                this.errors["mobile"].instep = "";
            }

            if (el.dataset.instep == 2) {
                this.clearEligibility();
            }

            if (el.name == "indication") {
                this.clearStepOne();
            }

            let rulesDataset = el.dataset.rules;
            let targetEl = el;

            // When we have radio we need to perform on the wrapping fieldset
            if (inputType == "radio") {
                let fieldsetEl = this.$el.querySelector(`fieldset[name="${el.name}"]`);
                targetEl = fieldsetEl;
                rulesDataset = fieldsetEl.dataset.rules;
            }

            if (rulesDataset) {
                const rules = rulesDataset.split(",");

                rules.forEach(rule => {
                    let isValid = false;

                    // Lets check if input is in custom API validation array
                    if (this.apiValidRules.includes(rule)) {
                        if (el.value) {
                            // Custom rules that need to hit API
                            if (rule == "validateCardId") {
                                if (this.cachedFormData.cardID != el.value) {
                                    self.isValidCardId = false;
                                    if (!self.isValidCardId) {
                                        checkCardId(el, this.baseEndPtUrl, this.validateCardUrl, settings)
                                            .then(result => {
                                                let errCode;
                                                result = JSON.parse(result);

                                                isValid = result.success ? result.success : false;

                                                if (result) {
                                                    errCode = result.code;
                                                } else {
                                                    errCode = "copayValidateAPIFail";
                                                }

                                                if (isValid) {
                                                    self.isValidCardId = isValid;
                                                    this.validateStep(3);
                                                }
                                                this.cachedFormData.cardID = el.value;
                                                this.mapErr(rule, targetEl, isValid, el, errCode);
                                            })
                                            .catch(err => {
                                                console.warn("Something went wrong validating card Id!", err); // eslint-disable-line
                                            });
                                    }
                                } else if (!self.isValidCardId) {
                                    // If value is invalid still show error
                                    this.mapErr(rule, targetEl, isValid, el);
                                }
                            }
                            if (rule == "validZip") {
                                if (this.cachedFormData.patientZIPEl === el.value) {
                                    let isValid = true;
                                    this.isValidZip = true;
                                    this.mapErr(rule, targetEl, isValid, el);
                                } else if (this.cachedFormData.patientZIPEl != el.value) {
                                    this.isValidZip = false;
                                    if (el.value.length === 5) {
                                        if (!this.isValidZip) {
                                            checkZip(el, this.baseEndPtUrl, this.baseZipValidatorUrl, settings)
                                                .then(result => {
                                                    isValid = result.isSuccess;
                                                    if (result.city) {
                                                        this.formData.zip = result.ZIPCode;
                                                        this.formData.city = result.city;
                                                        this.formData.state = result.state;
                                                    }
                                                    if (isValid) {
                                                        this.isValidZip = true;
                                                        this.cachedFormData.patientZIPEl = el.value;
                                                    }

                                                    this.mapErr(rule, targetEl, isValid, el);
                                                })
                                                .catch(err => {
                                                    console.warn("Something went wrong validating zipcode!", err); // eslint-disable-line
                                                });
                                        }
                                    } else if (!this.isValidZip) {
                                        // If value is invalid still show error
                                        this.mapErr(rule, targetEl, isValid, el);
                                    }
                                } else {
                                    this.mapErr(rule, targetEl, isValid, el);
                                }
                            }
                            if (rule == "safeToSend" && !localStorage.getItem("noSafeToSend")) {
                                if (this.cachedFormData.email != el.value) {
                                    this.isSafeToSend = false;
                                    if (!this.isSafeToSend) {
                                        checkSafeToSend(el, this.baseEndPtUrl, this.baseSafeToSendUrl)
                                            .then(result => {
                                                isValid = result;
                                                if (isValid) {
                                                    this.isSafeToSend = true;
                                                }
                                                this.cachedFormData.email = el.value;
                                                this.mapErr(rule, targetEl, isValid, el);
                                            })
                                            .catch(err => {
                                                console.warn("Something went checking if email safe to send!", err); // eslint-disable-line
                                            });
                                    }
                                } else if (!this.isSafeToSend) {
                                    // If value is invalid still show error
                                    this.mapErr(rule, targetEl, isValid, el);
                                }
                            }
                        }
                    } else {
                        isValid = validators[inputType][rule](el);
                        this.mapErr(rule, targetEl, isValid, el);
                    }
                });
            }
        },
        // Lets look if there is any other input with values -- mainly for browswer autofills
        validateAdjacent(targetEl, targetStep) {
            if (targetStep == this.formStep) {
                let items = document.querySelectorAll(`[data-step='${targetStep}'] input[data-rules], [data-step='${targetStep}'] fieldset[data-rules], [data-step='${targetStep}'] select[data-rules]`);

                for (let el of items) {
                    // ValidateInput needs to take in radio inputType
                    if (this.getValidatorType(el) == "fieldset") {
                        // lets pass in the first radio of fieldset ONLY for gender field
                        if (el.name == "gender") {
                            let checkedRadio = el.querySelector("input[type='radio']:checked");
                            if (checkedRadio) {
                                el = checkedRadio;
                            }
                        } else {
                            break;
                        }
                    }

                    if (el) {
                        if (el.name != targetEl.name) {
                            this.validateInput(el);
                        } else {
                            break;
                        }
                    }
                }
            }
        },
        mapErr(rule, targetEl, isValid, el, errCode) {
            let errorMsg;

            if (rule == "validateCardId") {
                if (errCode) {
                    errorMsg = this.config.errorMessages[rule][errCode];
                    // Default to copayValidateAPIFail if we dont have a errCode
                } else {
                    errorMsg = this.config.errorMessages[rule]["copayValidateAPIFail"];
                }
            } else {
                errorMsg = targetEl.hasAttribute("custom-required-message") ? this.getCustomErrorMsg(targetEl) : this.config.errorMessages[rule];
            }

            if (!isValid) {
                targetEl.classList.add("multistepform--invalid");
                this.makeError({
                    el: el,
                    errWith: rule,
                    hasError: true,
                    errorMessage: errorMsg
                });
            } else {
                targetEl.classList.remove("multistepform--invalid");
                this.makeError({ el: el, errWith: rule });
            }
        },

        // EVENT LISTENERS
        change: function(event) {
            const targetEl = event.target;
            const targetStep = targetEl.dataset.instep;
            // Lets only check for errors on text inputs when users focuses out
            this.validateInput(targetEl);
            this.validateAdjacent(targetEl, targetStep);
        },

        // HELPERS
        scrollTo(element, to, duration) {
            if (duration <= 0) return;

            var difference = to - element.scrollTop;

            var perTick = (difference / duration) * 10;

            setTimeout(function() {
                element.scrollTop = element.scrollTop + perTick;

                if (element.scrollTop === to) return;

                scrollTo(element, to, duration - 10);
            }, 10);
        },
        addRegionalModal(elem) {
            elem.dataset.toggle = "modal";
            elem.dataset.remote = "false";
            elem.dataset.target = "#regionMod";
        },
        getSignUpLink(emailLink) {
            let languagePath = "/";
            let indicationPath = this.formData.indication.toLowerCase();
            let signupPath = this.signUpPath;
            switch (this.formData.indication) {
                case "ASTHMA":
                    if (language == "es-US") {
                        languagePath = "/es/";
                        indicationPath = "asma";
                        signupPath = this.signUpPathASES;
                    }
                    break;
                case "ATOPICDERMATITIS":
                    if (language == "es-US") {
                        languagePath = "/es/";
                        signupPath = this.signUpPathADES;
                    } else {
                        signupPath = this.signUpPathAD;
                    }
                    break;
                case "PN":
                    indicationPath = "prurigo-nodularis";
                    signupPath = this.signUpPathPN;
                    if (language == "es-US") {
                        this.addRegionalModal(emailLink);
                    }
                    break;
                case "CSU":
                    indicationPath = "chronic-spontaneous-urticaria";
                    if (language == "es-US") {
                        this.addRegionalModal(emailLink);
                    }
                    break;
                default:
                    if (language == "es-US") {
                        this.addRegionalModal(emailLink);
                    }
                    break;
            }
            emailLink.href = languagePath + indicationPath + signupPath + "?copayIneligible=true";
        },
        getCardIndicationCode() {
            let code;

            switch (this.formData.indication) {
                case "ASTHMA":
                    code = "DUPIXENT_ASTHMA";
                    break;
                case "ATOPICDERMATITIS":
                    code = "DUPIXENT_ATOPIC";
                    break;
                case "CRSWNP":
                    code = "DUPIXENT_NASAL";
                    break;
                case "EOE":
                    code = "DUPIXENT_EOE";
                    break;
                case "BP":
                    code = "DUPIXENT_BP";
                    break;
            }
            return code;
        },
        getCustomErrorMsg(el) {
            return el.getAttribute("custom-required-message");
        },
        getValidatorType: function(ele) {
            const fieldType = ele.getAttribute("type") || ele.tagName.toLowerCase();
            const typesOfText = ["text", "email", "phone", "date"];
            return typesOfText.includes(fieldType) ? "string" : fieldType;
        },
        getUrlParam(param) {
            const urlParams = new URLSearchParams(window.location.search);
            const myParam = urlParams.get(param);
            return myParam;
        },
        getCheckedVal(el) {
            return el.checked;
        },
        buildFormData(token, recaptcha) {
            // RM this.mediaOriginCode of undefined
            if (!this.formData.mediaOriginCode || this.formData.mediaOriginCode == "undefined") {
                delete this.formData.mediaOriginCode;
            }

            let body = new URLSearchParams();

            for (const [key, value] of Object.entries(this.formData)) {
                body.append(key, value);
            }

            if (recaptcha) {
                body.append("g-recaptcha-response", token);
            }

            return body;
        },
        assignInputsInStep() {
            // Setting instep on all inputs to ensure they stay up-to-date in this.errors
            let steps = document.querySelectorAll(".multistepform__step");
            steps.forEach(step => {
                let currentStep = step.dataset.step;
                let elementsInSteps = [...step.querySelectorAll("input[type='radio']"), ...step.querySelectorAll("input[data-rules]"), ...step.querySelectorAll("fieldset[data-rules]"), ...step.querySelectorAll("select[data-rules]")];
                elementsInSteps.forEach(el => {
                    el.dataset.instep = currentStep;
                });
            });
        },
        defaultErrorMsg() {
            let self = this;
            Array.from(this.inputElements).forEach(function(el) {
                self.makeError({ el });
            });
        },
        makeError(obj) {
            let targetVal = obj.el.value ? obj.el.value : "";

            // Check if the indication has a fieldset on data attr -- this happens on preset
            if (obj.el.tagName == "FIELDSET" && obj.el.name == "indication") {
                targetVal = obj.el.dataset.value;
            }

            // Ensure checkbox get value of checked
            if (this.getValidatorType(obj.el) == "checkbox") {
                obj.el.value = this.getCheckedVal(obj.el);
                targetVal = this.getCheckedVal(obj.el);
            }

            if (obj.el) {
                // Do we have error obj for target item
                if (this.errors[obj.el.name]) {
                    // Does target item have a err already
                    if (this.errors[obj.el.name].hasError) {
                        if (obj.el.getAttribute("data-rules")) {
                            var errArr = obj.el.getAttribute("data-rules").split(",");
                            let indexTargetError = errArr.indexOf(obj.errWith);

                            let prevErr = this.errors[obj.el.name].errWith;
                            let indexPrevError = errArr.indexOf(prevErr);

                            // Based on rules attr lets show the left most rule's error
                            if (indexTargetError > indexPrevError) {
                                // The Error we are making is it the same as we already have?
                                if (obj.errWith != this.errors[obj.el.name].errWith) {
                                    obj = this.errors[obj.el.name];
                                }
                            }
                        }
                    }
                }
                // Error Defaults
                this.errors[obj.el.name] = {
                    hasError: false,
                    errorMessage: "",
                    instep: obj.el.dataset.instep,
                    value: targetVal,
                    ...obj
                };
            } else {
                console.warn("No name passed", obj); // eslint-disable-line
            }
        }
    };
});

function phoneInputMask() {
    //phone variable
    let phone = document.querySelector("#phone");
    // let phoneMask = phone.value.replace(/[_-]/g, "");
    //phone input mask
    Inputmask({ mask: "999-999-9999", placeholder: "XXX-XXX-XXXX" }).mask(phone);
    phone.addEventListener("blur", () => {
        if (phone.value == "") {
            phone.style.borderColor = "#d20606";
        }
        if (phone.value !== "" && !phone.value.includes("X")) {
            phone.style.borderColor = "unset";
        }
    });
}

async function checkCardId(el, urlBase, endpt) {
    let url = urlBase + endpt;

    const response = await fetch(url, {
        method: "POST",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded"
        },
        body: `cardID=${el.value}`
    });

    if (response.ok) {
        const text = await response.text();
        return text;
    }
    return false;
}

async function checkZip(el, urlBase, endpt) {
    let url = urlBase + endpt + el.value;
    const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
    });
    if (response.ok) {
        const data = await response.json();
        let errMsg = data.error;
        if (errMsg) {
            return false;
        } else {
            return data;
        }
    }
    return false;
}

async function checkSafeToSend(el, urlBase, endpt) {
    let url = urlBase + endpt + el.value;
    const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
    });

    if (response.ok) {
        const text = await response.text();
        return JSON.parse(text).valid;
    }

    return false;
}

async function submit(url, data) {
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data
    });

    if (response.ok) {
        const res = await response.text();
        return JSON.parse(res);
    }

    return false;
}
// disable enter key from submitting radio inputs
window.addEventListener(
    "keydown",
    function(e) {
        if (e.keyIdentifier == "U+000A" || e.keyIdentifier == "Enter" || e.key == "Enter") {
            if (e.target.type == "radio") {
                e.preventDefault();
                return false;
            }
        }
    },
    true
);
